import gpsPipelineEndpoints from './endpoints/gpsPipeline.endpoints';

/**
 * Process a single GPS point through the quality pipeline.
 * @param {Object} gpsPoint - GPS point data
 * @param {number} gpsPoint.person_id - Person identifier
 * @param {number} gpsPoint.latitude - Latitude
 * @param {number} gpsPoint.longitude - Longitude
 * @param {number} [gpsPoint.accuracy] - GPS accuracy in meters
 * @param {number} [gpsPoint.hdop] - Horizontal dilution of precision
 * @param {number} [gpsPoint.satellite_count] - Number of satellites
 * @param {number} [gpsPoint.battery_level] - Battery percentage
 * @param {string} [gpsPoint.timestamp] - ISO timestamp
 * @param {boolean} [gpsPoint.is_mock_location] - Mock location flag
 * @returns {Promise<Object|null>} Processed GPS point with quality scores
 */
export async function processGPSPoint(gpsPoint) {
  try {
    const response = await gpsPipelineEndpoints.processPoint(gpsPoint);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[processGPSPoint] Error:', error);
    throw error;
  }
}

/**
 * Process a batch of GPS points.
 * @param {number} personId - Person identifier
 * @param {Array<Object>} points - Array of GPS point objects
 * @param {boolean} [storeResults=true] - Whether to store results in Cosmos DB
 * @returns {Promise<Object|null>} Batch processing result
 */
export async function processGPSBatch(personId, points, storeResults = true) {
  try {
    const response = await gpsPipelineEndpoints.processBatch({ person_id: personId, points, store_results: storeResults });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[processGPSBatch] Error:', error);
    throw error;
  }
}

/**
 * Get GPS quality statistics for a person.
 * @param {number} personId - Person identifier
 * @param {number} [days=30] - Number of days to analyze
 * @returns {Promise<Object|null>} Quality statistics
 */
export async function getGPSQualityStats(personId, days = 30) {
  try {
    const response = await gpsPipelineEndpoints.getQualityStats({ params: { person_id: personId, days } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getGPSQualityStats] No stats found for person ${personId}`);
      return null;
    }
    console.error('[getGPSQualityStats] Error:', error);
    throw error;
  }
}

/**
 * Check GPS data for spoofing indicators.
 * @param {number} personId - Person identifier
 * @param {Array<Object>} [points] - Optional specific points to analyze
 * @param {number} [days=7] - Days of data to analyze if no points provided
 * @returns {Promise<Object|null>} Spoof analysis result
 */
export async function checkSpoofing(personId, points = null, days = 7) {
  try {
    const payload = { person_id: personId };
    if (points && points.length > 0) {
      payload.points = points;
    } else {
      payload.days = days;
    }

    const response = await gpsPipelineEndpoints.checkSpoofing(payload);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[checkSpoofing] No GPS data found for person ${personId}`);
      return null;
    }
    console.error('[checkSpoofing] Error:', error);
    throw error;
  }
}

/**
 * Get the most recent processed GPS point for a person.
 * @param {number} personId - Person identifier
 * @returns {Promise<Object|null>} Last processed GPS point
 */
export async function getLastProcessedPoint(personId) {
  try {
    const response = await gpsPipelineEndpoints.getLastProcessedPoint({ params: { person_id: personId } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getLastProcessedPoint] No points found for person ${personId}`);
      return null;
    }
    console.error('[getLastProcessedPoint] Error:', error);
    throw error;
  }
}

/**
 * Get spoof detection history for a person.
 * @param {number} personId - Person identifier
 * @param {number} [days=30] - Days of history to retrieve
 * @returns {Promise<Object|null>} Spoof analysis history
 */
export async function getSpoofHistory(personId, days = 30) {
  try {
    const response = await gpsPipelineEndpoints.getSpoofHistory({ params: { person_id: personId, days } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getSpoofHistory] No history found for person ${personId}`);
      return null;
    }
    console.error('[getSpoofHistory] Error:', error);
    throw error;
  }
}

/**
 * Generate sample GPS points for testing.
 * @param {number} personId - Person identifier
 * @param {number} baseLat - Base latitude
 * @param {number} baseLon - Base longitude
 * @param {number} [count=5] - Number of points to generate
 * @returns {Array<Object>} Array of GPS point objects
 */
export function generateSamplePoints(personId, baseLat, baseLon, count = 5) {
  const points = [];
  const now = new Date();

  for (let i = 0; i < count; i++) {
    const timestamp = new Date(now.getTime() + i * 5 * 60 * 1000); // 5 min intervals
    points.push({
      person_id: personId,
      latitude: baseLat + (i * 0.001) + (Math.random() * 0.0005 - 0.00025),
      longitude: baseLon + (i * 0.0008) + (Math.random() * 0.0005 - 0.00025),
      accuracy: 5 + Math.random() * 15,
      hdop: 1.0 + Math.random() * 2,
      satellite_count: 6 + Math.floor(Math.random() * 6),
      battery_level: 100 - i * 2,
      timestamp: timestamp.toISOString(),
    });
  }

  return points;
}

/**
 * Generate suspicious GPS points for spoof testing.
 * @param {number} personId - Person identifier
 * @returns {Array<Object>} Array of suspicious GPS points
 */
export function generateSuspiciousPoints(personId) {
  const now = new Date();
  return [
    {
      person_id: personId,
      latitude: 29.7604,
      longitude: -95.3698,
      accuracy: 10.0,
      timestamp: now.toISOString(),
    },
    {
      person_id: personId,
      latitude: 40.7128, // NYC - teleportation!
      longitude: -74.0060,
      accuracy: 10.0,
      timestamp: new Date(now.getTime() + 60000).toISOString(), // 1 min later
    },
  ];
}
